import { IConfigRetailUser, IConfigStateGroups, Result } from 'constants/interfaces'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { getContextName } from 'hooks/useContextName'
import { store } from 'config/redux'

const { getState } = store
const NO_VOTE_PLACEHOLDER = '—'

interface IProps {
  currentPollResults: Result[]
  notVotedParticipants?: string[]
  options: string[]
  fileName: string
  groups: IConfigStateGroups
  retailUsersObject: { [key: string]: IConfigRetailUser }
}
export const exportPollResultsToExcel = ({
  currentPollResults,
  notVotedParticipants = [],
  options,
  fileName,
  groups,
  retailUsersObject,
}: IProps) => {
  const { mappedGroupIdsByUsers } = getState().config.config

  // Check if any context has groups mapping
  const allContextIds = [...currentPollResults.map((item) => item.context_id), ...notVotedParticipants]
  const hasGroupsColumn = allContextIds.some((contextId) => mappedGroupIdsByUsers?.[contextId]?.length > 0)

  // Helper to get group names from group IDs
  const getGroupNames = (contextId: string): string => {
    const groupIds = mappedGroupIdsByUsers?.[contextId] || []
    if (!groupIds || groupIds.length === 0) return ''
    return groupIds
      .map((groupId: string) => groups?.[groupId]?.name || groupId)
      .filter(Boolean)
      .join(', ')
  }

  const workbook = new ExcelJS.Workbook()
  const sanitizedFileName = fileName.replace(/[\\/*?:[\]]/g, '_')
  const worksheet = workbook.addWorksheet(sanitizedFileName)

  const columns: { header: string; key: string; width: number }[] = [
    { header: 'Store Name', key: 'context_id', width: 30 },
  ]
  options.forEach((option, index) => {
    columns.push({ header: option, key: `option_${index}`, width: 15 })
  })
  if (hasGroupsColumn) {
    columns.push({ header: 'User groups', key: 'user_groups', width: 50 })
  }
  worksheet.columns = columns

  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).alignment = { horizontal: 'center' }
  worksheet.getRow(1).height = 20

  const styleRow = (row: ExcelJS.Row, opts?: { isNoVoteRow?: boolean }) => {
    row.eachCell((cell, colNumber) => {
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true,
      }
      cell.font = {
        size: 12,
        name: 'Arial',
      }
      if (colNumber === 1) {
        cell.font.bold = true
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF1F1F1' },
        }
      }
      if (cell.value === 'X') {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF90EE90' },
        }
      }
      if (opts?.isNoVoteRow && cell.value === NO_VOTE_PLACEHOLDER) {
        cell.font = {
          ...cell.font,
          color: { argb: 'FF999999' },
        }
      }
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
        left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
        bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } },
        right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      }
    })
  }

  // Сначала — проголосовавшие (есть poll_selections)
  const votedResults = currentPollResults.filter((r) => (r?.poll_selections?.length ?? 0) > 0)

  // Затем — не проголосовавшие: те у кого есть Result, но selections пустой, и те, у кого Result вообще нет
  const resultsNoVote = currentPollResults.filter((r) => (r?.poll_selections?.length ?? 0) === 0)
  const resultsNoVoteIds = new Set(resultsNoVote.map((r) => r.context_id))
  const notVotedWithoutResult = notVotedParticipants.filter((id) => !resultsNoVoteIds.has(id))

  votedResults.forEach((item) => {
    const rowData: Record<string, any> = { context_id: getContextName(item.context_id, groups, retailUsersObject!) }

    options.forEach((_, index) => {
      rowData[`option_${index}`] = item?.poll_selections?.includes(index) ? 'X' : ''
    })

    if (hasGroupsColumn) {
      rowData.user_groups = getGroupNames(item.context_id)
    }

    const row = worksheet.addRow(rowData)
    styleRow(row)
  })

  const addNoVoteRow = (contextId: string) => {
    const rowData: Record<string, any> = { context_id: getContextName(contextId, groups, retailUsersObject!) }
    options.forEach((_, index) => {
      rowData[`option_${index}`] = NO_VOTE_PLACEHOLDER
    })
    if (hasGroupsColumn) {
      rowData.user_groups = getGroupNames(contextId)
    }
    const row = worksheet.addRow(rowData)
    styleRow(row, { isNoVoteRow: true })
  }

  // Сначала те, кто "не голосовал", но result есть (порядок как в currentPollResults)
  resultsNoVote.forEach((r) => addNoVoteRow(r.context_id))
  // Потом те, у кого result вообще нет (порядок как в notVotedParticipants)
  notVotedWithoutResult.forEach((id) => addNoVoteRow(id))

  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer]), `${fileName}.xlsx`)
  })
}
